var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __rest = this && this.__rest || function (s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, TextField, Typography, styled } from '@mui/material';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { PlanType, PlanTypeCategory, StatusPermission } from '../../enum';
import { dateToString, shouldDisableDate } from '../../util';
import { useUserPermissionsAPI, useUserSessionAPI } from '../app/selectors';
import { Autocomplete, DatePicker, FieldLabel, FormField, NumberInput } from '../common';
import { ImportDataType, MAX_PERIOD_LENGTH, MONTHLY_PLAN_TYPES, getConnectionImportOptions, getImportDataTypeOptions, getIntegrationTypeFromImportType, getPlanTypeCategoryOptions, getPlanTypeOptions, isForecastImport } from './constants';
import { ForecastImportDialog } from './forecastImportDialog';
import { useClientIntegrationsAPI, useClientIntegrationsVerifiedAPI, useClientsAPI, useCreatePlanAPI, useUploadDataAPI, useValidatePlanAPI } from './selectors';
import { UploadSuccessStatusDialog } from './uploadStatusDialog';
import { UploadTemplateDialog } from './uploadTemplate';
var StyledForm = styled(Box)(function () {
  return {
    padding: 0
  };
});
export var defaultFormValues = {
  name: '',
  importData: ImportDataType.None,
  startDate: moment.utc().startOf('month'),
  planType: PlanType.Monthly,
  planTypeCategory: PlanTypeCategory.WorkVolume,
  periodLength: null,
  clientId: null
};
export var CreatePlan = function (_a) {
  var _b, _c;
  var handleImport = _a.handleImport,
    defaultValues = _a.defaultValues,
    formId = _a.formId,
    _d = _a.showSubmitButton,
    showSubmitButton = _d === void 0 ? true : _d,
    availableIntegrations = _a.availableIntegrations,
    isCreateDisabled = _a.isCreateDisabled;
  var navigate = useNavigate();
  var t = useTranslation(['myPlans', 'translation']).t;
  var userPermission = useUserPermissionsAPI();
  var _e = useState(false),
    isUpload = _e[0],
    setIsUpload = _e[1];
  var _f = useState(0),
    uploadDataId = _f[0],
    setUploadDataId = _f[1];
  var _g = useState(),
    uploadedFile = _g[0],
    setUploadedFile = _g[1];
  var _h = useState(false),
    isForecastDialogOpen = _h[0],
    setIsForecastDialogOpen = _h[1];
  var _j = useCreatePlanAPI(),
    createPlanData = _j[0],
    createPlan = _j[1],
    clearCreatePlanData = _j[2];
  var _k = useValidatePlanAPI(),
    validatePlanData = _k[0],
    validatePlan = _k[1],
    clearValidatePlanData = _k[2];
  var _l = useUploadDataAPI(),
    uploadData = _l[0],
    fetchUploadData = _l[1],
    clearUploadData = _l[2];
  var _m = useClientsAPI(),
    clientsData = _m[0],
    fetchClients = _m[1];
  var planFormData = useRef();
  useEffect(function () {
    fetchClients();
  }, [fetchClients]);
  var sessionData = useUserSessionAPI()[0].data;
  var isBpo = !!((_c = (_b = sessionData === null || sessionData === void 0 ? void 0 : sessionData.orgs) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.isBpo);
  // Embedded Genesys passes availableIntegrations and has no multi-geo UX: the Company/Client
  // selector is hidden and the (single) HQ client is auto-selected silently.
  var isEmbedded = !!availableIntegrations;
  var clients = useMemo(function () {
    var _a;
    return (_a = clientsData.data) !== null && _a !== void 0 ? _a : [];
  }, [clientsData.data]);
  // For non-BPO orgs, auto-select the HQ client
  var hqClient = useMemo(function () {
    return clients.find(function (c) {
      return c.isHqClient;
    });
  }, [clients]);
  var clientOptions = useMemo(function () {
    return clients.map(function (c) {
      return {
        id: c.id,
        name: c.clientName
      };
    });
  }, [clients]);
  var validateForm = yup.object().shape({
    name: yup.string().required().max(128).normalizeSpace().specialCharacter().quotesNotAllowed().label('Name'),
    importData: yup.string().required(),
    planType: yup.string().required().trim(),
    planTypeCategory: yup.string().required().trim(),
    clientId: yup.number().nullable().required(t('clientRequired')).label('Client'),
    periodLength: yup.number().nullable().when('importData', {
      is: function (value) {
        return value !== ImportDataType.Excel;
      },
      then: function (period) {
        return period.required();
      }
    }),
    startDate: yup.object().nullable().when('importData', {
      is: function (value) {
        return value !== ImportDataType.Excel;
      },
      then: function (date) {
        return date.required();
      }
    }),
    startDateError: yup.string().optional().when('importData', {
      is: function (value) {
        return value !== ImportDataType.Excel;
      },
      then: function (date) {
        return date.max(0, 'Invalid');
      }
    })
  });
  var _o = useForm({
      defaultValues: __assign(__assign({}, defaultFormValues), defaultValues),
      resolver: yupResolver(validateForm)
    }),
    control = _o.control,
    watch = _o.watch,
    setValue = _o.setValue,
    handleSubmit = _o.handleSubmit;
  var _p = watch(),
    planType = _p.planType,
    importData = _p.importData,
    clientId = _p.clientId;
  var _q = useClientIntegrationsAPI(clientId),
    clientIntegrationsData = _q[0],
    fetchClientIntegrations = _q[1];
  // The dropdown shows connections immediately from the stored list and the icons correct themselves shortly after.
  var _r = useClientIntegrationsVerifiedAPI(clientId),
    verifiedIntegrationsData = _r[0],
    fetchVerifiedIntegrations = _r[1];
  // Auto-select: HQ client for non-BPO orgs, or only available client when list has exactly one entry
  useEffect(function () {
    if (clientId) return;
    if (!isBpo && hqClient) {
      setValue('clientId', hqClient.id);
    } else if (clients.length === 1) {
      setValue('clientId', clients[0].id);
    }
  }, [isBpo, hqClient, clients, clientId, setValue]);
  useEffect(function () {
    if (clientId) {
      fetchClientIntegrations();
      fetchVerifiedIntegrations();
    }
  }, [clientId, fetchClientIntegrations, fetchVerifiedIntegrations]);
  // Build import data options. Embedded Genesys supplies its integrations directly via the
  // `availableIntegrations` prop and has no per-client integration flow, so prefer it when present.
  // Otherwise (multi-geo web app) derive the options from the selected client's integrations.
  var importDataOptions = useMemo(function () {
    var _a, _b;
    if (availableIntegrations) {
      return getImportDataTypeOptions(availableIntegrations);
    }
    if (!clientId) {
      return getConnectionImportOptions([]);
    }
    // One dropdown option per named connection (e.g. "Aspect UK", "Aspect Germany"). Prefer the
    // live-verified list once it arrives; until then use the stored list so options show immediately.
    return getConnectionImportOptions((_b = (_a = verifiedIntegrationsData.data) !== null && _a !== void 0 ? _a : clientIntegrationsData.data) !== null && _b !== void 0 ? _b : []);
  }, [availableIntegrations, clientId, clientIntegrationsData.data, verifiedIntegrationsData.data]);
  var selectedImportOption = useMemo(function () {
    return importDataOptions.find(function (o) {
      return o.id === importData;
    });
  }, [importDataOptions, importData]);
  // Integration dropdown handles validation visually, creation blocked at API-level
  // For BPO: all fields disabled until client is selected
  // For non-BPO: client is auto-selected, so fields are enabled
  var disableField = importData === ImportDataType.Excel;
  var disableFieldsUntilClient = isBpo && !clientId;
  var selectedClient = useMemo(function () {
    return clients.find(function (c) {
      return c.id === clientId;
    });
  }, [clients, clientId]);
  useEffect(function () {
    isCreateDisabled === null || isCreateDisabled === void 0 ? void 0 : isCreateDisabled(!(selectedClient === null || selectedClient === void 0 ? void 0 : selectedClient.configVersionId));
  }, [isCreateDisabled, selectedClient]);
  var handleClientChange = function (newClientId) {
    setValue('clientId', newClientId);
    // Reset import data when client changes
    setValue('importData', ImportDataType.None);
  };
  useEffect(function () {
    var _a, _b, _c, _d, _e, _f;
    if (createPlanData.data) {
      navigate("/wizard/".concat((_a = createPlanData.data) === null || _a === void 0 ? void 0 : _a.id));
    }
    if ((_b = uploadData.data) === null || _b === void 0 ? void 0 : _b.invalid) {
      navigate("/wizard/".concat((_c = uploadData.data) === null || _c === void 0 ? void 0 : _c.id));
    } else if (!((_d = uploadData.data) === null || _d === void 0 ? void 0 : _d.invalid) && !!((_e = uploadData.data) === null || _e === void 0 ? void 0 : _e.id)) {
      setUploadDataId((_f = uploadData.data) === null || _f === void 0 ? void 0 : _f.id);
    }
    return function () {
      clearUploadData();
      clearCreatePlanData();
    };
  }, [clearCreatePlanData, createPlanData.data, uploadData.data, navigate, clearUploadData]);
  useEffect(function () {
    if (validatePlanData.data && planFormData.current && isForecastImport(selectedImportOption)) {
      if (handleImport) {
        handleImport(planFormData.current);
      } else {
        setIsForecastDialogOpen(true);
      }
      clearValidatePlanData();
    }
  }, [validatePlanData.data, selectedImportOption, handleImport, clearValidatePlanData]);
  useEffect(function () {
    return clearValidatePlanData;
  }, [clearValidatePlanData]);
  // Validation flags a stale connection server-side
  useEffect(function () {
    if (validatePlanData.error && clientId && !availableIntegrations && (selectedImportOption === null || selectedImportOption === void 0 ? void 0 : selectedImportOption.isActive)) {
      fetchVerifiedIntegrations();
    }
  }, [validatePlanData.error, clientId, availableIntegrations, selectedImportOption === null || selectedImportOption === void 0 ? void 0 : selectedImportOption.isActive, fetchVerifiedIntegrations]);
  var isMonthlyPlanType = useMemo(function () {
    return MONTHLY_PLAN_TYPES.includes(planType);
  }, [planType]);
  var handleShouldDisableDate = useCallback(function (day) {
    return shouldDisableDate(planType, day);
  }, [planType]);
  var handleChangePlanType = function (newPlanType) {
    setValue('periodLength', null);
    setValue('startDate', MONTHLY_PLAN_TYPES.includes(newPlanType) ? moment.utc().startOf('month') : moment.utc());
  };
  var handleCreatePlan = handleSubmit(function (_a) {
    var startDate = _a.startDate,
      importValue = _a.importData,
      planTypeCategory = _a.planTypeCategory,
      formClientId = _a.clientId,
      values = __rest(_a, ["startDate", "importData", "planTypeCategory", "clientId"]);
    var selectedOption = importDataOptions.find(function (o) {
      return o.id === importValue;
    });
    var integrationType = getIntegrationTypeFromImportType(selectedOption);
    var integrationId = selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.integrationId;
    var configVersionId = selectedClient === null || selectedClient === void 0 ? void 0 : selectedClient.configVersionId;
    var payload = __assign(__assign(__assign(__assign({}, values), {
      startDate: dateToString(startDate),
      planTypeCategory: planTypeCategory,
      clientId: formClientId,
      configVersionId: configVersionId
    }), integrationType && {
      integrationType: integrationType
    }), integrationId != null && {
      integrationId: integrationId
    });
    if (importValue === ImportDataType.Excel) {
      setIsUpload(true);
    } else if (isForecastImport(selectedOption)) {
      planFormData.current = __assign({
        startDate: startDate,
        planTypeCategory: planTypeCategory,
        importData: importValue,
        clientId: formClientId,
        configVersionId: configVersionId,
        integrationType: integrationType,
        integrationId: integrationId
      }, values);
      validatePlan(payload);
    } else {
      createPlan(payload);
    }
  });
  var handleUploadComplete = handleSubmit(function (_a) {
    var startDate = _a.startDate,
      planTypeCategory = _a.planTypeCategory,
      formClientId = _a.clientId,
      values = __rest(_a, ["startDate", "planTypeCategory", "clientId"]);
    var configVersionId = selectedClient === null || selectedClient === void 0 ? void 0 : selectedClient.configVersionId;
    fetchUploadData(__assign(__assign({}, values), {
      startDate: dateToString(startDate),
      file: uploadedFile,
      importData: undefined,
      planTypeCategory: planTypeCategory,
      clientId: formClientId,
      configVersionId: configVersionId
    }));
    setIsUpload(!isUpload);
  });
  return _jsxs(_Fragment, {
    children: [_jsxs(StyledForm, __assign({
      component: "form",
      onSubmit: handleCreatePlan,
      id: formId
    }, {
      children: [_jsxs(Box, __assign({
        className: "cinareo-create-plan-grid"
      }, {
        children: [!isEmbedded && _jsxs(Box, {
          children: [_jsx(FieldLabel, {
            required: true,
            label: t('client', {
              ns: 'myPlans',
              defaultValue: 'Company / Client'
            })
          }), _jsx(FormField, __assign({
            required: true,
            name: "clientId",
            control: control
          }, {
            children: _jsx(Autocomplete, {
              options: clientOptions,
              getOptionValue: function (o) {
                return o.id;
              },
              disabled: !isBpo,
              onChange: handleClientChange,
              "data-testid": "clientSelect",
              placeholder: t('translation:layout.pleaseSelect')
            })
          }))]
        }), _jsxs(Box, {
          children: [_jsx(FieldLabel, {
            required: true,
            label: t('nameOfPlan')
          }), _jsx(FormField, __assign({
            required: true,
            name: "name",
            control: control
          }, {
            children: _jsx(TextField, {
              inputProps: {
                maxLength: 128
              },
              fullWidth: true,
              disabled: disableFieldsUntilClient
            })
          }))]
        }), _jsxs(Box, {
          children: [_jsx(FieldLabel, {
            required: true,
            label: t('importData')
          }), _jsx(FormField, __assign({
            required: true,
            name: "importData",
            control: control
          }, {
            children: _jsx(Autocomplete, {
              options: importDataOptions,
              disabled: disableFieldsUntilClient,
              renderOption: function (props, option) {
                return _jsxs(Box, __assign({
                  component: "li"
                }, props, {
                  sx: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }
                }, {
                  children: [_jsx(Typography, {
                    children: option.name
                  }), option.isActive === false && _jsx(Typography, __assign({
                    color: "warning.main",
                    sx: {
                      ml: 'auto',
                      display: 'flex'
                    }
                  }, {
                    children: _jsx(FontAwesomeIcon, {
                      icon: "triangle-exclamation"
                    })
                  }))]
                }));
              }
            })
          }))]
        }), _jsxs(Box, {
          children: [_jsx(FieldLabel, {
            required: true,
            label: t('planStartDate')
          }), _jsx(FormField, __assign({
            required: true,
            name: "startDate",
            control: control
          }, {
            children: _jsx(DatePicker, {
              control: control,
              disabled: !!disableField || disableFieldsUntilClient,
              disableHighlightToday: isMonthlyPlanType,
              shouldDisableDate: handleShouldDisableDate
            })
          }))]
        }), _jsxs(Box, {
          children: [_jsx(FieldLabel, {
            required: true,
            label: t('planType')
          }), _jsx(FormField, __assign({
            required: true,
            name: "planType",
            control: control
          }, {
            children: _jsx(Autocomplete, {
              options: getPlanTypeOptions(),
              getOptionValue: function (o) {
                return o.value;
              },
              onChange: handleChangePlanType,
              "data-testid": "planTypeSelect",
              disabled: !!disableField || disableFieldsUntilClient
            })
          }))]
        }), _jsxs(Box, {
          children: [_jsx(FieldLabel, {
            required: true,
            label: t('length')
          }), _jsx(FormField, __assign({
            required: true,
            name: "periodLength",
            control: control
          }, {
            children: _jsx(NumberInput, {
              placeholder: t("numberPlaceholder".concat(planType)),
              min: 1,
              max: MAX_PERIOD_LENGTH[planType],
              fullWidth: true,
              disabled: !!disableField || disableFieldsUntilClient
            })
          }))]
        }), _jsxs(Box, __assign({
          sx: {
            gridColumn: '1 / 2'
          }
        }, {
          children: [_jsx(FieldLabel, {
            required: true,
            label: t('createPlanPlanInputLabel', {
              defaultValue: 'Plan input'
            })
          }), _jsx(FormField, __assign({
            required: true,
            name: "planTypeCategory",
            control: control
          }, {
            children: _jsx(Autocomplete, {
              options: getPlanTypeCategoryOptions(),
              getOptionValue: function (o) {
                return o.value;
              },
              disabled: !!disableField || disableFieldsUntilClient
            })
          }))]
        }))]
      })), showSubmitButton && _jsx(Box, __assign({
        sx: {
          display: 'flex',
          justifyContent: 'flex-end',
          mt: 3
        }
      }, {
        children: _jsx(LoadingButton, __assign({
          disabled: (userPermission === null || userPermission === void 0 ? void 0 : userPermission.capacityPermission) !== StatusPermission.Edit,
          variant: "contained",
          type: "submit",
          sx: {
            height: 'fit-content',
            width: 'max-content',
            minWidth: '9rem'
          },
          loading: !!createPlanData.requesting || !!uploadData.requesting || !!validatePlanData.requesting
        }, {
          children: t('createPlan')
        }))
      }))]
    })), _jsx(UploadTemplateDialog, {
      open: isUpload,
      onClose: function () {
        return setIsUpload(!isUpload);
      },
      handleUploadComplete: handleUploadComplete,
      uploadedFile: uploadedFile,
      setUploadedFile: setUploadedFile,
      setValue: setValue
    }), _jsx(UploadSuccessStatusDialog, {
      uploadDataId: uploadDataId
    }), planFormData.current && _jsx(ForecastImportDialog, {
      open: isForecastDialogOpen,
      onClose: function () {
        return setIsForecastDialogOpen(false);
      },
      importType: planFormData.current.importData,
      planFormData: planFormData.current
    })]
  });
};